import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, AlertCircle, Search, Users, Link2, TrendingUp } from "lucide-react";

type Row = Record<string, string>;

interface AnalysisData {
  domain: string;
  database: string;
  overview: Row | null;
  backlinks: Row | null;
  keywords: Row[];
  competitors: Row[];
  gaps: (Row & { _competitor?: string })[];
  fetchedAt: string;
}

const pick = (row: Row | null | undefined, ...keys: string[]) => {
  if (!row) return "—";
  for (const k of keys) if (row[k] !== undefined && row[k] !== "") return row[k];
  return "—";
};

const num = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("pl-PL") : v;
};

const kdBadge = (kd: string) => {
  const n = Number(kd);
  if (!Number.isFinite(n)) return <Badge variant="secondary">—</Badge>;
  if (n < 30) return <Badge className="bg-emerald-600 hover:bg-emerald-600/90">Łatwe {Math.round(n)}</Badge>;
  if (n < 50) return <Badge className="bg-amber-500 hover:bg-amber-500/90">Średnie {Math.round(n)}</Badge>;
  if (n < 70) return <Badge className="bg-orange-600 hover:bg-orange-600/90">Trudne {Math.round(n)}</Badge>;
  return <Badge variant="destructive">Bardzo trudne {Math.round(n)}</Badge>;
};

export default function CompetitorPanel() {
  const [domain, setDomain] = useState("naklejki-3d.com.pl");
  const [database, setDatabase] = useState("pl");
  const [data, setData] = useState<AnalysisData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data: res, error: err } = await supabase.functions.invoke("semrush-analysis", {
      body: { domain, database },
    });
    if (err) setError(err.message);
    else if ((res as any)?.error) setError((res as any).error);
    else setData(res as AnalysisData);
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Analiza konkurencji i pozycjonowania</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Dane z Semrush (wyniki organiczne Google). Sprawdź kto konkuruje w produkcji naklejek 3D i jakie
          frazy możesz przejąć.
        </p>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <Label htmlFor="cp-domain">Domena</Label>
            <Input id="cp-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="naklejki-3d.com.pl" />
          </div>
          <div className="w-28">
            <Label htmlFor="cp-db">Rynek</Label>
            <Input id="cp-db" value={database} onChange={(e) => setDatabase(e.target.value)} placeholder="pl" />
          </div>
          <Button onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Analizuję..." : "Analizuj"}
          </Button>
        </div>
      </Card>

      {error && (
        <Card className="p-4 border-destructive/50 bg-destructive/5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-destructive">Nie udało się pobrać danych</p>
            <p className="text-sm text-muted-foreground mt-1">{error}</p>
          </div>
        </Card>
      )}

      {data && (
        <>
          {/* Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4">
              <p className="text-xs uppercase text-muted-foreground">Frazy organiczne</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                {num(pick(data.overview, "Organic Keywords", "Or"))}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase text-muted-foreground">Szac. ruch / mies.</p>
              <p className="text-2xl font-bold text-primary mt-1">
                {num(pick(data.overview, "Organic Traffic", "Ot"))}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase text-muted-foreground">Authority Score</p>
              <p className="text-2xl font-bold text-foreground mt-1">{pick(data.backlinks, "ascore")}</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs uppercase text-muted-foreground">Domeny linkujące</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                {num(pick(data.backlinks, "domains_num"))}
              </p>
            </Card>
          </div>

          {/* Competitors */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Users className="w-5 h-5" /> Konkurenci ({data.competitors.length})
            </h3>
            {data.competitors.length === 0 ? (
              <Card className="p-4"><p className="text-sm text-muted-foreground">Brak danych o konkurentach dla tej domeny.</p></Card>
            ) : (
              <Card className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr className="text-left">
                      <th className="p-3 font-medium">Domena</th>
                      <th className="p-3 font-medium">Wspólne frazy</th>
                      <th className="p-3 font-medium">Frazy organiczne</th>
                      <th className="p-3 font-medium">Szac. ruch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.competitors.map((c, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-3">
                          <a href={`https://${pick(c, "Domain", "Dn")}`} target="_blank" rel="noopener"
                            className="text-foreground hover:text-primary font-medium break-all">
                            {pick(c, "Domain", "Dn")}
                          </a>
                        </td>
                        <td className="p-3">{num(pick(c, "Common Keywords", "Np"))}</td>
                        <td className="p-3">{num(pick(c, "Organic Keywords", "Or"))}</td>
                        <td className="p-3">{num(pick(c, "Organic Traffic", "Ot"))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>

          {/* Keyword gaps */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Luki słów kluczowych ({data.gaps.length})
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              Frazy, na które rankują konkurenci, a Twoja strona nie. Zacznij od tych z oznaczeniem „Łatwe”.
            </p>
            {data.gaps.length === 0 ? (
              <Card className="p-4"><p className="text-sm text-muted-foreground">Nie znaleziono luk — albo brak danych o konkurentach.</p></Card>
            ) : (
              <Card className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr className="text-left">
                      <th className="p-3 font-medium">Fraza</th>
                      <th className="p-3 font-medium">Wyszukiwania / mies.</th>
                      <th className="p-3 font-medium">Trudność</th>
                      <th className="p-3 font-medium">Rankuje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.gaps.map((g, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-3 font-medium text-foreground">{pick(g, "Keyword", "Ph")}</td>
                        <td className="p-3">{num(pick(g, "Search Volume", "Nq"))}</td>
                        <td className="p-3">{kdBadge(pick(g, "Keyword Difficulty", "Kd"))}</td>
                        <td className="p-3 text-muted-foreground break-all">{g._competitor ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>

          {/* Own keywords */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Search className="w-5 h-5" /> Twoje frazy ({data.keywords.length})
            </h3>
            {data.keywords.length === 0 ? (
              <Card className="p-4"><p className="text-sm text-muted-foreground">Semrush nie widzi jeszcze fraz dla tej domeny.</p></Card>
            ) : (
              <Card className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr className="text-left">
                      <th className="p-3 font-medium">Fraza</th>
                      <th className="p-3 font-medium">Pozycja</th>
                      <th className="p-3 font-medium">Wyszukiwania</th>
                      <th className="p-3 font-medium">Trudność</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.keywords.map((k, i) => {
                      const pos = Number(pick(k, "Position", "Po"));
                      return (
                        <tr key={i} className="border-t border-border">
                          <td className="p-3 font-medium text-foreground">{pick(k, "Keyword", "Ph")}</td>
                          <td className="p-3">
                            <Badge variant={pos <= 3 ? "default" : pos <= 10 ? "secondary" : "outline"}>
                              {Number.isFinite(pos) ? pos : "—"}
                            </Badge>
                          </td>
                          <td className="p-3">{num(pick(k, "Search Volume", "Nq"))}</td>
                          <td className="p-3">{kdBadge(pick(k, "Keyword Difficulty", "Kd"))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            )}
          </div>

          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Link2 className="w-3 h-3" />
            Źródło: Semrush, rynek „{data.database}”. Wartości są szacunkami dla wyników organicznych Google.
          </p>
        </>
      )}
    </div>
  );
}
